"use client"

import { useEffect, useState, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { getTrackingData, getJourneyReport, initializeTracking, trackPageVisit } from '@/lib/user-tracking'
import { getAdvancedTrackingData } from '@/lib/advanced-tracking'

export function BounceTracker() {
  const pathname = usePathname()
  const [hasEngaged, setHasEngaged] = useState(false)
  const [startTime] = useState(Date.now())
  const [formData, setFormData] = useState<any>({})
  const hasSentBounce = useRef(false)
  const sendingBounce = useRef(false)

  // Initialize tracking on mount (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      initializeTracking()
      trackPageVisit()
    }
  }, [pathname])

  const [engagementData, setEngagementData] = useState({
    scrolled: false,
    clicked: false,
    maxScroll: 0,
    clickCount: 0,
    currentPage: pathname,
    formInteracted: false
  })

  // Track any user engagement
  useEffect(() => {
    const handleScroll = () => {
      setHasEngaged(true)
      const scrollPercentage = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100)
      setEngagementData(prev => ({
        ...prev,
        scrolled: true,
        maxScroll: Math.max(prev.maxScroll, scrollPercentage)
      }))
    }

    const handleClick = () => {
      setHasEngaged(true)
      setEngagementData(prev => ({
        ...prev,
        clicked: true,
        clickCount: prev.clickCount + 1
      }))
    }

    // Track form inputs globally
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        setHasEngaged(true)
        setEngagementData(prev => ({
          ...prev,
          formInteracted: true
        }))

        const value = target.value

        // Capture form data by name/id - accumulate, don't overwrite
        if (target.name || target.id) {
          const fieldName = target.name || target.id
          setFormData((prev: any) => ({
            ...prev,
            [fieldName]: value || prev[fieldName] // Keep existing if new is empty
          }))
        }

        // Also capture by value patterns for TypeForm that doesn't use standard field names
        if (value) {
          // Email pattern detection
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (emailRegex.test(value)) {
            setFormData((prev: any) => ({ ...prev, email: value }))
          }

          // Phone pattern detection (10+ digits)
          const phoneRegex = /^\d{10,}$/
          const cleanedValue = value.replace(/[\s()-]/g, '')
          if (phoneRegex.test(cleanedValue)) {
            setFormData((prev: any) => ({ ...prev, phone: value }))
          }
        }

        // Enhanced field detection
        const nameAttr = target.name?.toLowerCase() || ''
        const idAttr = target.id?.toLowerCase() || ''
        const placeholder = target.placeholder?.toLowerCase() || ''
        const label = target.getAttribute('aria-label')?.toLowerCase() || ''

        // Email detection - accumulate data
        if (value && (target.type === 'email' || nameAttr.includes('email') || idAttr.includes('email') || placeholder.includes('email'))) {
          setFormData((prev: any) => ({ ...prev, email: value || prev.email }))
        }

        // Phone detection - accumulate data
        if (value && (target.type === 'tel' || nameAttr.includes('phone') || idAttr.includes('phone') || placeholder.includes('phone') || placeholder.includes('mobile'))) {
          setFormData((prev: any) => ({ ...prev, phone: value || prev.phone }))
        }

        // Name detection - accumulate and merge
        if (value) {
          if (nameAttr === 'name' || idAttr === 'name' || placeholder === 'first last' || placeholder.includes('your name')) {
            setFormData((prev: any) => ({ ...prev, name: value || prev.name }))
          } else if (nameAttr.includes('firstname') || idAttr === 'firstname' || placeholder.includes('first name')) {
            setFormData((prev: any) => ({
              ...prev,
              firstName: value || prev.firstName,
              // If we have firstName and lastName, combine into name
              name: prev.name || (value && prev.lastName ? `${value} ${prev.lastName}` : prev.name)
            }))
          } else if (nameAttr.includes('lastname') || idAttr === 'lastname' || placeholder.includes('last name')) {
            setFormData((prev: any) => ({
              ...prev,
              lastName: value || prev.lastName,
              // If we have firstName and lastName, combine into name
              name: prev.name || (prev.firstName && value ? `${prev.firstName} ${value}` : prev.name)
            }))
          }
        }

        // Budget detection - keep if filled
        if (value && (nameAttr.includes('budget') || idAttr === 'budget' || placeholder.includes('budget') || placeholder.includes('150,000'))) {
          setFormData((prev: any) => ({ ...prev, budget: value || prev.budget }))
        }

        // Timeframe detection - keep if filled
        if (value && (nameAttr.includes('timeframe') || idAttr === 'timeframe')) {
          setFormData((prev: any) => ({ ...prev, timeframe: value || prev.timeframe }))
        }

        // Land/property detection - accumulate
        if (idAttr === 'hasland' || nameAttr.includes('hasland')) {
          const hasLandValue = target.type === 'checkbox' ? target.checked : value
          if (hasLandValue !== undefined) {
            setFormData((prev: any) => ({ ...prev, hasLand: hasLandValue }))
          }
        }
        if (value && (idAttr === 'landaddress' || nameAttr.includes('landaddress') || placeholder.includes('city or zip'))) {
          setFormData((prev: any) => ({ ...prev, landAddress: value || prev.landAddress }))
        }
        if (value && (idAttr === 'interestedarea' || nameAttr.includes('interestedarea') || placeholder.includes('any area'))) {
          setFormData((prev: any) => ({ ...prev, interestedArea: value || prev.interestedArea }))
        }

        // Floor plan detection - accumulate
        if (value && (idAttr === 'floorplan' || nameAttr.includes('floorplan') || placeholder.includes('model name'))) {
          setFormData((prev: any) => ({ ...prev, floorplan: value || prev.floorplan }))
        }

        // Message/notes detection - accumulate both if different
        if (value && (target.tagName === 'TEXTAREA' || nameAttr.includes('message') || nameAttr.includes('notes') || idAttr === 'notes' || idAttr === 'message')) {
          setFormData((prev: any) => {
            // If it's a different message, append it
            const existingMessage = prev.message || prev.notes || ''
            const newMessage = existingMessage && value !== existingMessage
              ? `${existingMessage}\n---\n${value}`
              : value
            return { ...prev, message: newMessage, notes: newMessage }
          })
        }
      }
    }

    // Don't auto-engage just from time - require actual interaction
    // const engagementTimer = setTimeout(() => {
    //   setHasEngaged(true)
    // }, 3000)

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('click', handleClick)
    window.addEventListener('input', handleInput)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('click', handleClick)
      window.removeEventListener('input', handleInput)
      // clearTimeout(engagementTimer)
    }
  }, [])

  // Track bounce when user leaves
  useEffect(() => {
    const sendBounceData = async () => {
      // Only track if there's meaningful engagement
      const hasMeaningfulEngagement =
        engagementData.formInteracted || // Started filling form
        (engagementData.scrolled && engagementData.maxScroll > 10) || // Scrolled more than 10%
        engagementData.clickCount > 2 || // Multiple clicks
        Object.keys(formData).length > 0 // Has any form data

      // Prevent duplicate sends with double-check locking
      if (hasEngaged && hasMeaningfulEngagement && !hasSentBounce.current && !sendingBounce.current) {
        sendingBounce.current = true

        // Double check after setting the flag
        if (hasSentBounce.current) {
          sendingBounce.current = false
          return
        }

        hasSentBounce.current = true

        const timeOnSite = Math.floor((Date.now() - startTime) / 1000)
        const minutes = Math.floor(timeOnSite / 60)
        const seconds = timeOnSite % 60

        try {
          const trackingData = getTrackingData()
          const advancedTracking = await getAdvancedTrackingData()

          const bounceData = {
            partialFormData: formData,
            formType: engagementData.formInteracted ? 'Site-Wide Form Interaction' : 'Page Visit Only',
            currentStep: null,
            completedFields: Object.keys(formData),
            trackingData,
            advancedTracking,
            journeyReport: getJourneyReport(),
            bounceReason: engagementData.formInteracted ? 'Started form but abandoned' : 'Browsed site without form interaction',
            timeOnSite: `${minutes}m ${seconds}s`,
            engagement: engagementData,
            currentPage: pathname
          }

          // Use sendBeacon for reliable delivery on page unload
          const blob = new Blob([JSON.stringify(bounceData)], { type: 'application/json' })
          navigator.sendBeacon('/api/track-bounce', blob)

        } catch (error) {
          console.error('Error sending bounce data:', error)
        }
      }
    }

    const handleBeforeUnload = () => {
      sendBounceData()
    }

    // Only track when actually leaving the page, not switching tabs
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handleBeforeUnload)
    }
  }, [hasEngaged, formData, startTime, engagementData]) // Removed pathname - we don't want to trigger on route change

  // Reset on route change - but don't send bounce for internal navigation
  useEffect(() => {
    // Just reset the flags for the new page, don't trigger a send
    // The actual bounce will only happen on beforeunload/pagehide
    const timer = setTimeout(() => {
      hasSentBounce.current = false
      sendingBounce.current = false
    }, 1000) // Small delay to prevent immediate re-send

    return () => clearTimeout(timer)
  }, [pathname])

  // Allow sending updated data after significant changes
  useEffect(() => {
    // If user adds significant new data after first bounce, allow another send
    const significantDataAdded = Object.keys(formData).filter(key => formData[key]).length
    if (hasSentBounce.current && significantDataAdded > 3) {
      // Reset after collecting more data
      setTimeout(() => {
        hasSentBounce.current = false
        sendingBounce.current = false
      }, 30000) // Wait 30 seconds before allowing another send
    }
  }, [formData])

  return null // This component doesn't render anything
}